import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthFacade } from '../auth';
import { JwtData } from '../auth/state/auth.reducer';
import { subscribeOnce } from '../_helpers/rxjs.helper';
import { AgrDebtorsVendorsStatus, AgreementData, AgreementsService, AgreementStatus, AgreementVendorDebtorData } from '../_services/agreements.service';
import jwt_decode from "jwt-decode";

export interface CreateInvoiceComponentData{
  agreementId: string;
}

@Component({
  selector: 'app-createinvoice',
  templateUrl: './createinvoice.component.html',
  styleUrls: ['./createinvoice.component.scss']
})

export class CreateInvoiceComponent implements OnInit {
  @Input() selectedValue: string;
  @Input() selectedFile: any;

  files: any[] = [];
  form!: FormGroup;
  vendorsDebtors: AgreementVendorDebtorData[] = [];
  agreements: AgreementData[] = [];
  agreementTitles: string[] = [];
  agreementVendorsDebtorsNames: string[] = [];
  agreementTitle = '';
  id = '';
  customerId = '';

  //spinner properties
  color = 'primary';
  mode = 'indeterminate';
  value = 50;
  displayProgressSpinner = false;
  spinnerWithoutBackdrop = false;
  disableSubmitButton = true;

  constructor(
    public dialogRef: MatDialogRef<CreateInvoiceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateInvoiceComponentData,
    private formBuilder: FormBuilder,
    private agreementsService: AgreementsService,
    private authFacade: AuthFacade,
    private toastr: ToastrService
  ) { 
    this.authFacade.token$.subscribe((token => {
      let decodedToken: JwtData = jwt_decode(token)
      this.customerId = decodedToken.customerId;
    }));
  }

  ngOnInit(): void {
    this.id = this.data.agreementId;
    this.agreementsService.getAgreements().subscribe((result) => {
      this.agreements = result.filter(x => x.status == AgreementStatus.Valid && new Date(x.contractTerm) > new Date());
      if(this.id)
        this.agreementTitle = this.agreements.filter(x => x.systemId == this.id)[0].id;
      this.agreementTitles = this.agreements.map(x => x.id);
      this.refreshForm();

    });

    this.agreementsService.getVendorsDebtors().subscribe((result) => {
      this.vendorsDebtors = result;
      if(this.agreementTitle){
        this.agreementVendorsDebtorsNames = this.vendorsDebtors.filter((x => x.agreementId == this.agreementTitle && x.status == AgrDebtorsVendorsStatus.Valid)).map(x => x.name);
      }
    });

   this.refreshForm();
  }

  refreshForm(){
    this.form = this.formBuilder.group({ 
      agreementNo: [ this.agreementTitle || '', Validators.required],
      debtorNo: ['', Validators.required],
      invoiceDate:['', Validators.required],
      invoiceNo: ['', Validators.required],
      invoiceAmountInclVAT: ['', [Validators.required, Validators.pattern(/^\d+(?:[,.]\d+)?$/)]],
      invoiceDueDate: ['', Validators.required]
    })

    this.form.controls['agreementNo'].valueChanges.subscribe((result) => {
      if(result){
        this.agreementTitle = result;
        this.id = this.agreements.filter(x => x.id == result)[0].systemId;
        this.agreementVendorsDebtorsNames = this.vendorsDebtors.filter((x => x.agreementId == result)).map(x => x.name);
        this.form.controls['debtorNo'].setValue('');
      }
    });
  }

  onSubmit(){
    if(this.form.valid){
      this.showProgressSpinner();
      let agreementVendorDebtor = this.form.value.debtorNo;
      this.form.value.debtorNo = this.vendorsDebtors.filter(x => x.agreementId == this.agreementTitle && x.name == agreementVendorDebtor)[0].id; 
      this.agreementsService.createInvoice({...this.form.value }).subscribe((result => {
        if(result.data){
          if(this.files.length > 0){
            const formData = new FormData();
            for (const file of this.files) {
              formData.append(file.name, file);
            }
  
            let agreementId = this.id;
  
            this.agreementsService.uploadDocument(formData, agreementId, 'Invoice_'+this.form.value.invoiceNo).subscribe(result => {
              this.hideProgressSpinner();
    
              if(result){
                this.showToasterSuccess();
                this.files = [];
                this.selectedValue = "";
                this.dialogRef.close();
              }
              else{
                this.showToasterError();
                this.dialogRef.close();
              }
            });
          }
          else{
            this.hideProgressSpinner();
            this.showToasterSuccess();
            this.dialogRef.close();
          }
        }
        else{
          this.hideProgressSpinner();
          this.showToasterError()
          this.dialogRef.close();
        }
      }));
    }
  }

  onFileDropped(file) {
    this.prepareFilesList(file);
  }

  fileBrowseHandler(file) {
    this.prepareFilesList(file);
  }

  deleteFile(index: number) {
    this.files.splice(index, 1);
  }

  prepareFilesList(files: Array<any>) {
    for (const item of files) {
      this.files.push(item);
    }
  }

  formatBytes(bytes) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = 0;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }


  highlightRow(file) {
    this.selectedFile = file.name;
  }

  removeHighlightedRow(){
    this.selectedFile = null;
  }

  //toast
  showToasterSuccess(){
    this.toastr.success('Invoice created successfully.', 'Success', {
      positionClass: 'toast-bottom-center',
      progressAnimation: 'decreasing',
      timeOut: 5000,
    });
  }

  showToasterError(){
    this.toastr.error('Failed to create invoice.', 'Error', {
      positionClass: 'toast-bottom-center',
      progressAnimation: 'decreasing',
      timeOut: 5000,
    });
  }

  //spinner methods
  showProgressSpinner = () => {
    this.displayProgressSpinner = true;
  };

  hideProgressSpinner = () => {
    this.displayProgressSpinner = false;
  };
}